import * as React from 'react';
import { polyfill } from 'mobile-drag-drop';
import * as _ from 'underscore';
import { GameCategoryDropData, GameCategoryDropState } from '../Games.types';
import PrimaryButton from '../../atoms/PrimaryButton';
import { useLocation } from 'react-router-dom';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import { useAutoSaveOnPageLeave, useParentContainerDisableScrolling } from '../../../store/hooks';
import { GameStateDB } from '../../../data/datatypes';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import draggableIcon from '../../../static/icons/icon_draggable.svg';
import dropBasketIcon from '../../../static/icons/drop_basket.svg';

type Properties = {
    data: GameCategoryDropData;
    setNextButtonLabel: (label: string | undefined) => void;
};

const CategoryDrop = (props: Properties) => {
    const path = useLocation().pathname;
    const dbId: `game_${string}` = `game_${getUrlWithTransientStateProperties(path)}`;

    const [doneItemIds, setDoneItemIds] = React.useState<number[]>([]);

    const dragIdxRef = React.useRef<number>(-1);
    const rightBasketRef = React.useRef<boolean>(false);

    React.useEffect(() => {
        polyfill({
            dragStartConditionOverride: (event: TouchEvent) => {
                return (event.target as any).nodeName !== 'IMG';
            }
        });
        window.addEventListener('touchmove', function () { return; }); // iOS fix for polyfill, see https://github.com/timruffles/mobile-drag-drop/issues/77
        return () => {
            window.removeEventListener('touchmove', function () { return; });
        };
    }, []);

    useParentContainerDisableScrolling();

    useAutoSaveOnPageLeave(
        [doneItemIds],
        async () => {
            const gameState: GameStateDB = {
                _id: dbId,
                state: {
                    doneItemIds
                }
            };
            await saveGameState(gameState);
        },
        [path],
        async () => {
            const state = await getGameState(dbId);
            if (state) {
                setDoneItemIds((state.state as GameCategoryDropState).doneItemIds);
            }
        }
    );

    // set next-Button label
    React.useEffect(() => {
        props.setNextButtonLabel('Spiel beenden');
        return () => {
            props.setNextButtonLabel(undefined);
        };
    }, [props.setNextButtonLabel]);

    function addDraggingClass(event: any) {
        if (event?.currentTarget?.classList) {
            event.currentTarget.classList.add('dragging');
        }
    }

    function removeDraggingClass(event: any) {
        if (event?.currentTarget?.classList) {
            event.currentTarget.classList.remove('dragging');
        }
    }

    function addDragOverClass(event: any) {
        if (event?.currentTarget?.classList) {
            event.currentTarget.classList.add('dragover');
        }
    }

    function removeDragOverClass(event: any) {
        if (event?.currentTarget?.classList) {
            event.currentTarget.classList.remove('dragover');
        }
    }

    function animateBasket(event: any) {
        const animationClass = rightBasketRef.current ? 'right-basket-animation' : 'wrong-basket-animation';
        const element = event?.currentTarget;
        if (element?.classList) {
            element.classList.add(animationClass);
            setTimeout(() => { element.classList.remove(animationClass); }, 1000);
        }
    }

    let itemElements: any = _.reduce(
        props.data.items,
        (acc: any[], item, i) => {
            if (doneItemIds.indexOf(i) === -1) {
                acc.push(
                    <div
                        className="category-draggable-item"
                        draggable={true}
                        onDragStart={(ev: any) => {
                            dragIdxRef.current = i;
                            addDraggingClass(ev);
                            ev.dataTransfer.setData('idx', i);
                        }}
                        onDragEnd={(ev: any) => {
                            removeDraggingClass(ev);
                        }}
                        key={i}
                    >
                        <img className="icon-draggable" src={draggableIcon} />
                        <div className="category-draggable-item-text">{item.title}</div>
                    </div>
                );
            }
            return acc;
        },
        []
    );

    let categoryElements = props.data.categories.map(
        (cat, i) => {
            return (
                <div
                    className="category-dropzone"
                    onDragOver={(ev: any) => {
                        let item = _.find(props.data.items, (el, j) => j === dragIdxRef.current);
                        if (item !== undefined && item.category === cat.id) {
                            rightBasketRef.current = true;
                        } else {
                            rightBasketRef.current = false;
                        }
                        ev.preventDefault();
                    }}
                    onDragLeave={(ev: any) => {
                        removeDragOverClass(ev);
                    }}
                    onDragEnter={(ev: any) => {
                        addDragOverClass(ev);
                        let item = _.find(props.data.items, (el, j) => j === dragIdxRef.current);
                        if (item !== undefined && item.category === cat.id) {
                            rightBasketRef.current = true;
                        } else {
                            rightBasketRef.current = false;
                        }
                        ev.preventDefault();
                    }}
                    onDrop={(ev: any) => {
                        ev.preventDefault();
                        removeDragOverClass(ev);
                        animateBasket(ev);
                        if (rightBasketRef.current) {
                            setDoneItemIds(prev => [...prev, dragIdxRef.current]);
                        }
                    }}
                    key={i}
                >
                    <img
                        className="drop-basket-img"
                        src={dropBasketIcon}
                    />
                    <span className="drop-basket-title">{cat.title}</span>
                </div>
            );
        }
    );

    let successMsg: any = undefined;
    if (itemElements.length === 0) {
        successMsg = <div>
            <div className="category-drop-success">Super, du hast alle Begriffe richtig zugeordnet!</div>
            <PrimaryButton
                label="Spiel neu starten"
                onClick={() => setDoneItemIds([])}
            />
        </div>;
    }

    return (

        <div className="category-drop swiper-no-swiping">
            <div>
                <h1>{props.data.caption}</h1>
                <p><TextWithReferences text={props.data.desc || ''} /></p>
            </div>
            {successMsg}
            <div className="drag-container">{itemElements}</div>
            <div className="drop-container">{categoryElements}</div>
        </div>

    );
};

export default CategoryDrop;